import { getWorkerUrl } from '@affine/env/worker';

import type { OPFSWriteCommand, OPFSWriteResult } from './opfs-write-protocol';

export type OPFSWritable = WritableStream<Uint8Array> & {
  write(data: string | Uint8Array): Promise<void>;
  seek(position: number): Promise<void>;
  close(): Promise<void>;
};

/** Same OPFS files and streaming contract on browsers with either write API. */
export async function createOPFSWritable(
  handle: FileSystemFileHandle,
  keepExistingData = false
): Promise<OPFSWritable> {
  if (typeof handle.createWritable === 'function') {
    return handle.createWritable({ keepExistingData });
  }
  if (typeof Worker === 'undefined') {
    throw new DOMException(
      'OPFS writing is not supported',
      'NotSupportedError'
    );
  }
  // Older WebKit cannot clone file handles across threads. Resolve its OPFS
  // path here, then obtain the native handle inside the dedicated worker.
  const root = await navigator.storage.getDirectory();
  const path = await root.resolve(handle);
  if (!path?.length)
    throw new DOMException('File is not in OPFS', 'NotFoundError');
  const worker = new Worker(getWorkerUrl('opfs-write'));
  let pending:
    | { resolve: () => void; reject: (error: DOMException) => void }
    | undefined;
  let failure: DOMException | undefined;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const fail = (error: DOMException) => {
    failure = error;
    clearTimeout(timeout);
    const request = pending;
    pending = undefined;
    worker.terminate();
    request?.reject(error);
  };
  worker.onmessage = (event: MessageEvent<OPFSWriteResult>) => {
    clearTimeout(timeout);
    const request = pending;
    pending = undefined;
    if (event.data.ok) request?.resolve();
    else {
      failure = new DOMException(event.data.message, event.data.name);
      worker.terminate();
      request?.reject(failure);
    }
  };
  worker.onerror = () =>
    fail(new DOMException('OPFS worker could not start', 'UnknownError'));
  worker.onmessageerror = () =>
    fail(new DOMException('OPFS worker message failed', 'UnknownError'));
  const send = (command: OPFSWriteCommand) =>
    new Promise<void>((resolve, reject) => {
      if (failure) {
        reject(failure);
        return;
      }
      if (pending) {
        reject(new DOMException('Concurrent file write', 'InvalidStateError'));
        return;
      }
      pending = { resolve, reject };
      timeout = setTimeout(
        () => fail(new DOMException('OPFS worker timed out', 'TimeoutError')),
        30_000
      );
      try {
        worker.postMessage(command);
      } catch (error) {
        fail(
          new DOMException(
            error instanceof Error ? error.message : 'OPFS message failed',
            'UnknownError'
          )
        );
      }
    });
  await send({ type: 'open', path, keepExistingData });
  const stream = new WritableStream<Uint8Array>(
    {
      // Await each acknowledgement: never buffer a whole download in memory.
      write: data => send({ type: 'write', data }),
      close: async () => {
        try {
          await send({ type: 'close' });
        } finally {
          worker.terminate();
        }
      },
      abort: async () => {
        try {
          await send({ type: 'abort' });
        } finally {
          worker.terminate();
        }
      },
    },
    { highWaterMark: 1 }
  );
  return Object.assign(stream, {
    write: async (data: string | Uint8Array) => {
      const writer = stream.getWriter();
      try {
        await writer.write(
          typeof data === 'string' ? new TextEncoder().encode(data) : data
        );
      } finally {
        writer.releaseLock();
      }
    },
    seek: (position: number) => send({ type: 'seek', position }),
    close: async () => {
      const writer = stream.getWriter();
      try {
        await writer.close();
      } finally {
        writer.releaseLock();
      }
    },
  });
}
